import {loginApi} from 'sources/api';
import {List} from 'immutable';
import {ResponseStatus} from 'utils/ResponseStatus';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {joinSharedAreas, SharedAreas} from '@components/call-card/SharedHighlights.utils';
import {getSpeakerDescription} from '@components/call-card/CallSpeaker.utils';
import {AccountId} from 'records/User';
import {Call} from '@store/call-card/Call';
import CallCardActions, {CallCardActionsInterface} from '@store/call-card/CallCardActions';
import {CallAsset, Segment, Speaker, TranscriptBlock} from '@store/call-card/CallCard.types';

interface ExternalLoginResponse {
  status: ResponseStatus;
  callId?: FileUploadId;
  sharedAreas: SharedAreas;
  accountId: AccountId | null;
}

interface ExternalShareBundle {
  call: {total: number; results: any[]; fields: any[]; requestBody: any};
  assets: CallAsset[];
  speakers: Omit<Speaker, 'description'>[];
  segments: Segment[];
  transcript: TranscriptBlock[];
}

export class ExternalCallCardSource {
  static login(permalink: string, password?: string): Promise<ExternalLoginResponse> {
    return loginApi
      .post(`/login/share/${permalink}`, password ? {password} : undefined, {validateStatus: null})
      .then(res => ({
        status: res.status,
        callId: res.data?.fileUploadId,
        sharedAreas: joinSharedAreas(List(res.data?.ranges?.map(r => [r.start, r.end]) ?? [])),
        accountId: res.data?.accountId ?? null,
      }));
  }

  static getBundle(permalink: string): Promise<ExternalShareBundle> {
    return loginApi.getJson(`/external-share-bundle/${permalink}`);
  }

  static hydrateStoreFromBundle(bundle: ExternalShareBundle, callId: FileUploadId): void {
    // Cast to CallCardActionsInterface to access the auto-generated *Success handlers
    // which exist at runtime but aren't in the default createActions return type.
    const actions = CallCardActions as unknown as CallCardActionsInterface;

    if (bundle.call.results.length > 0) {
      actions.getCallSuccess(new Call(bundle.call.results[0]));
    }
    actions.getCallSourceSuccess([bundle.assets, callId]);
    const speakers = List(
      bundle.speakers.map((s, i) => ({
        ...s,
        description: getSpeakerDescription(s as Speaker, i + 1),
      }))
    );
    actions.getSpeakersSuccess([speakers, callId]);
    actions.getSegmentsSuccess([List(bundle.segments), callId]);
    actions.getTranscriptSuccess([List(bundle.transcript), callId]);
  }
}
